import type { Ethogram, Subject } from '../../lib/api/endpoints.js';
import type { PreparedAudio } from '../../lib/audio/prepare.js';

/**
 * The A–B–C answers, as the form holds them.
 *
 * Everything except `signalType` and `nextAction` is optional. That is the
 * progressive-disclosure contract: a contribution is valid with the three
 * things that make it scientifically useful, and every other field is depth a
 * contributor can add later from the timeline.
 */
export interface AbcAnswers {
  subjectId: string;

  // A — what triggered it
  triggerType: string;
  antecedentTerms: string[];
  antecedentFree: string;

  // B — the signal
  signalType: string;
  bodyLanguage: string[];
  intensity: number;

  // C — what happened next
  nextAction: string;
  consequenceTerms: string[];
  didResolve: boolean | null;

  // Interpretation — explicitly a hypothesis
  proposedMeaning: string;
  meaningCategory: string;
  certainty: 'high' | 'medium' | 'guess';
  basis: string;

  // Ethics
  welfareAttested: boolean;
  humanVoicesPresent: boolean;
  humanAudioConsent: boolean;
  indigenousKnowledge: boolean;
}

export const emptyAnswers = (subjectId: string): AbcAnswers => ({
  subjectId,
  triggerType: 'external_stimulus',
  antecedentTerms: [],
  antecedentFree: '',
  signalType: '',
  bodyLanguage: [],
  intensity: 3,
  nextAction: '',
  consequenceTerms: [],
  didResolve: null,
  proposedMeaning: '',
  meaningCategory: '',
  certainty: 'medium',
  basis: 'repeated_observation',
  welfareAttested: false,
  humanVoicesPresent: false,
  humanAudioConsent: false,
  indigenousKnowledge: false,
});

/** The minimum a submission needs. Kept deliberately short. */
export function isSubmittable(answers: AbcAnswers): boolean {
  return (
    answers.signalType !== '' &&
    answers.nextAction.trim() !== '' &&
    answers.welfareAttested &&
    (!answers.humanVoicesPresent || answers.humanAudioConsent)
  );
}

interface BuildInput {
  answers: AbcAnswers;
  subject: Subject;
  audio: PreparedAudio;
  assetId: string;
  ethogram: Ethogram;
  contributorId: string;
  contributorRole: string;
  licence: string;
}

/**
 * Assembles a FaunaCore record from the form.
 *
 * Three things the server owns and this deliberately does not set:
 * `observation_id`, `created_at`, and the final stored `uri`. A client that
 * chose its own observation id could overwrite another contributor's record by
 * guessing one, and a client-supplied timestamp would let the corpus be
 * backdated.
 *
 * `ring_buffer_prefix_ms` is **0** and that is meaningful, not a placeholder.
 * An uploaded file has no retroactive capture: the recording began when someone
 * pressed record, which means the moments before the signal are missing. The
 * distinction between that and a true ring-buffer capture matters
 * scientifically and must never be inferred or fudged — it is the whole reason
 * the field exists.
 */
export function buildRecord(input: BuildInput): Record<string, unknown> {
  const { answers, subject, audio, assetId, ethogram, contributorId, contributorRole, licence } =
    input;

  return {
    schema_version: 'faunacore-0.1',

    contributor: {
      id: contributorId,
      role: contributorRole,
    },

    subject: {
      id: subject.id,
      taxon: {
        scientific_name: subject.scientificName,
        ...(subject.commonName ? { common_name: subject.commonName } : {}),
      },
      ...(subject.breedOrMorph ? { breed_or_morph: subject.breedOrMorph } : {}),
      ...(subject.sex ? { sex: subject.sex } : {}),
      ...(subject.lifeStage ? { life_stage: subject.lifeStage } : {}),
      ...(subject.captiveStatus ? { captive_status: subject.captiveStatus } : {}),
      // These are named animals with a history, which is what makes repeated
      // observations analysable as repeated measures rather than as
      // independent samples.
      known_individual: true,
    },

    capture: {
      device: navigator.userAgent.slice(0, 180),
      sample_rate_hz: audio.sampleRateHz,
      channels: audio.channels,
      ring_buffer_prefix_ms: 0,
      environment: {
        setting: 'indoor_home',
      },
    },

    media: [
      {
        asset_id: assetId,
        type: 'audio',
        // The server rewrites this to the canonical storage location. Sent
        // because the schema requires the field, not because the client knows
        // where the file lives.
        uri: `voxfauna://media/${assetId}`,
        duration_ms: audio.durationMs,
        checksum_sha256: audio.checksumSha256,
        license: licence,
      },
    ],

    segments: [
      {
        segment_id: `seg_${assetId.replace(/^med_/, '')}`,
        asset_id: assetId,
        start_ms: 0,
        end_ms: audio.durationMs,
        annotations: [
          {
            annotator_id: contributorId,
            annotator_type: 'human',
            signal_type: answers.signalType,
            intensity: answers.intensity,
            // A contributor's confidence in their own labelling. Coarse on
            // purpose — asking for a number between 0 and 1 produces noise.
            confidence: answers.certainty === 'high' ? 0.9 : answers.certainty === 'medium' ? 0.6 : 0.3,
          },
        ],
      },
    ],

    antecedent: {
      trigger_type: answers.triggerType,
      ...(answers.antecedentFree.trim()
        ? { description_free: answers.antecedentFree.trim() }
        : {}),
      coded_terms: answers.antecedentTerms,
    },

    signal_summary: {
      primary_modality: 'vocal',
      co_occurring_body_language: answers.bodyLanguage,
    },

    consequence: {
      animal_next_action: answers.nextAction.trim(),
      coded_terms: answers.consequenceTerms,
      ...(answers.didResolve !== null ? { did_it_resolve: answers.didResolve } : {}),
    },

    interpretation: {
      // Required by the schema. Falls back to the contributor's honest
      // "I don't know" rather than inventing a meaning on their behalf —
      // a fabricated hypothesis is worse than a recorded absence of one.
      proposed_meaning: answers.proposedMeaning.trim() || 'Not sure',
      ...(answers.meaningCategory ? { meaning_category: answers.meaningCategory } : {}),
      contributor_certainty: answers.certainty,
      basis: answers.basis,
    },

    verification: {
      status: 'pending',
      independent_agreements: 0,
      independent_disagreements: 0,
      inter_rater_alpha: null,
    },

    consent_and_ethics: {
      animal_welfare_attestation: answers.welfareAttested,
      // Constant, not a form field. A contributor cannot submit a provoked
      // recording through this app, and there is no control that would let
      // them try — the schema itself rejects anything else.
      provocation_used: false,
      human_voices_present: answers.humanVoicesPresent,
      ...(answers.humanVoicesPresent ? { human_audio_consent: answers.humanAudioConsent } : {}),
      indigenous_knowledge_flag: answers.indigenousKnowledge,
      release_license: licence,
    },

    ethogram_version: ethogram.ethogram_version,
  };
}
